import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import { RoleIcon } from '@/components/common/RoleIcon';
import { useCurrentSchool } from '@/hooks/useCurrentSchool';
import { useRole } from '@/hooks/useRole';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';

interface WorkspaceCardProps {
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  /** Plain-text trail for the current page (e.g. "Finance & Fees › Receipts") — display only, not a nav control. */
  trail?: string | null;
}

/**
 * Sidebar summary of where the user is working: the active role on a brand
 * band (with the collapse toggle docked in its corner), the current school
 * beneath, and — once inside a section — a plain-text trail of the current
 * page. Display only: role switching stays in the account menu, the
 * clickable step-by-step breadcrumb stays in the header.
 */
export function WorkspaceCard({ collapsed = false, onToggleCollapse }: WorkspaceCardProps) {
  const theme = useTheme();
  const { roleLabel, roleIcon } = useRole();
  const { currentSchool } = useCurrentSchool();
  const schoolName = currentSchool ? currentSchool.aliasName || currentSchool.schoolName : null;
  const band = `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`;

  const icon = (
    <Box
      sx={{
        width: 34,
        height: 34,
        borderRadius: 1.25,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        bgcolor: alpha(theme.palette.primary.contrastText, 0.18),
        color: 'primary.contrastText',
      }}
    >
      <RoleIcon icon={roleIcon} sx={{ fontSize: 20 }} />
    </Box>
  );

  if (collapsed) {
    return (
      <Tooltip title={onToggleCollapse ? 'Expand sidebar' : (schoolName ? `${roleLabel} · ${schoolName}` : roleLabel)} placement="right">
        <Box
          role={onToggleCollapse ? 'button' : undefined}
          tabIndex={onToggleCollapse ? 0 : undefined}
          aria-label={onToggleCollapse ? 'Expand sidebar' : undefined}
          onClick={onToggleCollapse}
          onKeyDown={(e) => {
            if (onToggleCollapse && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              onToggleCollapse();
            }
          }}
          sx={{ display: 'flex', justifyContent: 'center', px: 1.5, pb: 1, flexShrink: 0, cursor: onToggleCollapse ? 'pointer' : 'default' }}
        >
          <Box sx={{ borderRadius: 1.5, background: band, p: 0.375 }}>
            <IconButton>
              <MenuOpenIcon sx={{ fontSize: 18, color: 'primary.contrastText', transform: 'rotate(180deg)' }} />
            </IconButton>
            </Box>
        </Box>
      </Tooltip>
    );
  }

  return (
    <Box
      sx={{
        mx: 1.5,
        mb: 1,
        flexShrink: 0,
        borderRadius: 2,
        overflow: 'hidden',
        border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.5, py: 1.25, background: band }}>
        {icon}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontSize: '0.625rem',
              fontWeight: 700,
              letterSpacing: 0.8,
              textTransform: 'uppercase',
              color: alpha(theme.palette.primary.contrastText, 0.8),
              lineHeight: 1.4,
            }}
          >
            Workspace
          </Typography>
          <Typography noWrap sx={{ fontSize: '0.9375rem', fontWeight: 700, color: 'primary.contrastText', lineHeight: 1.3 }}>
            {roleLabel}
          </Typography>
        </Box>
        {onToggleCollapse ? (
          <Tooltip title="Collapse sidebar">
            <IconButton
              onClick={onToggleCollapse}
              aria-label="Collapse sidebar"
              size="small"
              sx={{
                flexShrink: 0,
                color: 'primary.contrastText',
                bgcolor: alpha(theme.palette.primary.contrastText, 0.16),
                '&:hover': { bgcolor: alpha(theme.palette.primary.contrastText, 0.28) },
              }}
            >
              <MenuOpenIcon sx={{ fontSize: 16, color: 'primary.contrastText' }} />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>
    </Box>
  );
}
